import { memo, useMemo } from 'react';
import HtmlKatex from '../HtmlKatex';

/**
 * Preview prof (readonly) : affiche la solution complète du programme de construction.
 * - SVG avec points nommés, segments prédessinés
 * - Tous les cercles attendus en couleur pleine
 * - Liste des consignes avec chips de couleur
 *
 * @param {Object} viewBox - { w, h }
 * @param {Array<{name, x, y}>} points
 * @param {Array<{type, from, to}>} preShapes
 * @param {Array<{id, consigne, color}>} tasks
 * @param {number} pxPerCm
 * @param {string} bonneReponse - JSON string "{taskId: {cx,cy,r}}"
 */
const ProgrammeConstructionRenderer = memo(function ProgrammeConstructionRenderer({
    viewBox, points = [], preShapes = [], tasks = [], pxPerCm = 30, bonneReponse, light,
    // false en correction élève : la liste des verdicts y remplace les consignes
    showConsignes = true,
}) {
    const { w: W = 600, h: H = 450 } = viewBox || {};

    const expected = useMemo(() => {
        try { return JSON.parse(bonneReponse || '{}'); } catch { return {}; }
    }, [bonneReponse]);

    const labelColor = light ? 'text-gray-500' : 'text-slate-400';
    const tagColor = light ? 'text-gray-600' : 'text-slate-300';

    return (
        <div className="space-y-2">
            {showConsignes && (
                <span className={`text-[10px] font-semibold uppercase tracking-wider block ${labelColor}`}>
                    Programme de construction
                </span>
            )}

            {/* Liste des consignes avec chips colorés */}
            {showConsignes && (
                <ul className="space-y-1.5">
                    {tasks.map((t) => (
                        <li key={t.id} className="flex items-start gap-2 text-xs sm:text-sm">
                            <span
                                className="inline-block w-3 h-3 rounded-full flex-shrink-0 mt-1"
                                style={{ background: t.color }}
                            />
                            <HtmlKatex
                                html={t.consigne}
                                className={`${tagColor} min-w-0 flex-1 break-words`}
                            />
                        </li>
                    ))}
                </ul>
            )}

            {/* Canvas SVG avec solution */}
            <div className="w-full rounded-xl border bg-white overflow-hidden">
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
                    {/* Grille de fond */}
                    <defs>
                        <pattern id="pc-grid-preview" width="30" height="30" patternUnits="userSpaceOnUse">
                            <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#e0e0da" strokeWidth="0.4" />
                        </pattern>
                    </defs>
                    <rect width={W} height={H} fill="url(#pc-grid-preview)" />

                    {/* Segments prédessinés (line) et demi-droites (ray) */}
                    {preShapes.map((s, i) => {
                        if (s.type === 'line') {
                            const from = Array.isArray(s.from)
                                ? { x: s.from[0], y: s.from[1] }
                                : points.find(p => p.name === s.from);
                            const to = Array.isArray(s.to)
                                ? { x: s.to[0], y: s.to[1] }
                                : points.find(p => p.name === s.to);
                            if (!from || !to) return null;
                            return (
                                <line
                                    key={`preshape-${i}`}
                                    x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                                    stroke="#2c2c2a" strokeWidth="1.5" strokeLinecap="round"
                                />
                            );
                        }
                        if (s.type === 'ray') {
                            const from = points.find(p => p.name === s.from);
                            if (!from) return null;
                            const length = s.length ?? 220;
                            const a = -(s.angleDeg ?? 0) * Math.PI / 180;
                            const end = { x: from.x + length * Math.cos(a), y: from.y + length * Math.sin(a) };
                            const dx = end.x - from.x, dy = end.y - from.y;
                            const len = Math.hypot(dx, dy) || 1;
                            const ux = dx / len, uy = dy / len;
                            const tipLen = 10;
                            const tipPts = `${end.x},${end.y} ${end.x - tipLen * ux + tipLen * 0.35 * uy},${end.y - tipLen * uy - tipLen * 0.35 * ux} ${end.x - tipLen * ux - tipLen * 0.35 * uy},${end.y - tipLen * uy + tipLen * 0.35 * ux}`;
                            return (
                                <g key={`preshape-${i}`}>
                                    <line x1={from.x} y1={from.y} x2={end.x} y2={end.y} stroke="#2c2c2a" strokeWidth="1.5" strokeLinecap="round" />
                                    <polygon points={tipPts} fill="#2c2c2a" />
                                    {s.labelEnd && (
                                        <text x={end.x + 6} y={end.y + 4} fontSize="13" fontWeight="600" fill="#2c2c2a" fontFamily="system-ui,sans-serif">{s.labelEnd}</text>
                                    )}
                                </g>
                            );
                        }
                        return null;
                    })}

                    {/* Formes attendues (solution) — cercle ou droite selon shape.type */}
                    {tasks.map((t) => {
                        const shape = expected[t.id];
                        if (!shape) return null;
                        if (shape.type === 'ray') {
                            // La réponse élève ne stocke que { type:'ray', angleDeg } (sans `from`) :
                            // on retrouve le sommet d'origine dans la spec de la tâche (t.shape.from).
                            const fromName = shape.from ?? t.shape?.from;
                            const from = points.find(p => p.name === fromName);
                            if (!from) return null;
                            const a = -(shape.angleDeg ?? 0) * Math.PI / 180;
                            // Étend aux bords du viewBox avec une longueur max raisonnable
                            // pour laisser de la place au label (inverse de l'extend complet).
                            const dx = Math.cos(a), dy = Math.sin(a);
                            const ts = [];
                            if (Math.abs(dx) > 1e-6) { const t1 = (0 - from.x) / dx; if (t1 > 0) ts.push(t1); const t2 = (W - from.x) / dx; if (t2 > 0) ts.push(t2); }
                            if (Math.abs(dy) > 1e-6) { const t1 = (0 - from.y) / dy; if (t1 > 0) ts.push(t1); const t2 = (H - from.y) / dy; if (t2 > 0) ts.push(t2); }
                            const tMax = ts.length ? Math.min(...ts) - 15 : 220;
                            const end = { x: from.x + tMax * dx, y: from.y + tMax * dy };
                            const len = Math.hypot(end.x - from.x, end.y - from.y) || 1;
                            const ux = (end.x - from.x) / len, uy = (end.y - from.y) / len;
                            const tipLen = 10;
                            const tipPts = `${end.x},${end.y} ${end.x - tipLen * ux + tipLen * 0.35 * uy},${end.y - tipLen * uy - tipLen * 0.35 * ux} ${end.x - tipLen * ux - tipLen * 0.35 * uy},${end.y - tipLen * uy + tipLen * 0.35 * ux}`;
                            return (
                                <g key={`solution-${t.id}`}>
                                    <line x1={from.x} y1={from.y} x2={end.x} y2={end.y}
                                        stroke={t.color} strokeWidth="2" strokeLinecap="round" />
                                    <polygon points={tipPts} fill={t.color} />
                                    <circle cx={from.x} cy={from.y} r={3} fill={t.color} />
                                    {(shape.labelEnd ?? t.shape?.labelEnd) && (
                                        <text x={end.x + 6} y={end.y + 4} fontSize="13" fontWeight="600" fill={t.color} fontFamily="system-ui,sans-serif">{shape.labelEnd ?? t.shape?.labelEnd}</text>
                                    )}
                                </g>
                            );
                        }
                        if (shape.type === 'segment') {
                            // `segment` était absent de ce renderer : la forme tombait
                            // dans la branche « cercle » avec cx/cy/r indéfinis, donc
                            // rien ne s'affichait côté prof. Le renderer d'impression
                            // (renderConstructionFigure) traitait déjà 'line' ET 'segment'.
                            // `pointsOnly` : la tâche fait cliquer deux points sans
                            // tracer le trait qui les joint (cf. gen_657 Ex. 4).
                            const rPoint = t.pointsOnly ? 5 : 3;
                            return (
                                <g key={`solution-${t.id}`}>
                                    {!t.pointsOnly && (
                                        <line x1={shape.p1.x} y1={shape.p1.y} x2={shape.p2.x} y2={shape.p2.y}
                                            stroke={t.color} strokeWidth="2" strokeLinecap="round" />
                                    )}
                                    <circle cx={shape.p1.x} cy={shape.p1.y} r={rPoint} fill={t.color} />
                                    <circle cx={shape.p2.x} cy={shape.p2.y} r={rPoint} fill={t.color} />
                                </g>
                            );
                        }
                        if (shape.type === 'line') {
                            // Étend la droite aux bords du viewBox (paramétrage simple)
                            const dx = shape.p2.x - shape.p1.x;
                            const dy = shape.p2.y - shape.p1.y;
                            const cands = [];
                            if (Math.abs(dx) > 1e-6) {
                                cands.push((0 - shape.p1.x) / dx, (W - shape.p1.x) / dx);
                            }
                            if (Math.abs(dy) > 1e-6) {
                                cands.push((0 - shape.p1.y) / dy, (H - shape.p1.y) / dy);
                            }
                            const pts = cands
                                .map(t2 => ({ x: shape.p1.x + t2 * dx, y: shape.p1.y + t2 * dy, t: t2 }))
                                .filter(p => p.x >= -0.01 && p.x <= W + 0.01 && p.y >= -0.01 && p.y <= H + 0.01)
                                .sort((a, b) => a.t - b.t);
                            const ends = pts.length >= 2
                                ? { x1: pts[0].x, y1: pts[0].y, x2: pts[pts.length - 1].x, y2: pts[pts.length - 1].y }
                                : { x1: shape.p1.x, y1: shape.p1.y, x2: shape.p2.x, y2: shape.p2.y };
                            return (
                                <g key={`solution-${t.id}`}>
                                    <line x1={ends.x1} y1={ends.y1} x2={ends.x2} y2={ends.y2}
                                        stroke={t.color} strokeWidth="2" strokeLinecap="round" />
                                    <circle cx={shape.p1.x} cy={shape.p1.y} r={3} fill={t.color} />
                                    <circle cx={shape.p2.x} cy={shape.p2.y} r={3} fill={t.color} />
                                </g>
                            );
                        }
                        // Cercle (pas de champ type : backward-compat)
                        return (
                            <g key={`solution-${t.id}`}>
                                <circle cx={shape.cx} cy={shape.cy} r={shape.r}
                                    fill="none" stroke={t.color} strokeWidth="2" />
                                <circle cx={shape.cx} cy={shape.cy} r={2.5} fill={t.color} />
                            </g>
                        );
                    })}

                    {/* `extraShapes` : formes auto-tracées à la validation côté élève,
                        typiquement les côtés qui referment le triangle une fois le
                        dernier sommet placé. Elles ne figurent PAS dans `expected`,
                        si bien que le corrigé prof montrait une figure inachevée :
                        les arcs et le sommet, mais pas le triangle. */}
                    {tasks.map((t) => (t.extraShapes || []).map((s, i) => {
                        if (!s || !s.p1 || !s.p2) return null;
                        if (s.type !== 'segment' && s.type !== 'line') return null;
                        return (
                            <line
                                key={`extra-${t.id}-${i}`}
                                x1={s.p1.x} y1={s.p1.y} x2={s.p2.x} y2={s.p2.y}
                                stroke={s.color || t.color} strokeWidth="2" strokeLinecap="round"
                            />
                        );
                    }))}

                    {/* Points nommés */}
                    {points.map((p, i) => (
                        <g key={`point-${i}`}>
                            <circle cx={p.x} cy={p.y} r={4} fill="#2c2c2a" />
                            <text
                                x={p.x + 9} y={p.y - 5}
                                fontSize="14" fontWeight="500"
                                fill="#2c2c2a"
                                fontFamily="system-ui,sans-serif"
                                style={{ userSelect: 'none' }}
                            >
                                {p.name}
                            </text>
                        </g>
                    ))}
                </svg>
            </div>

            {pxPerCm && (
                <p className={`text-[10px] italic ${labelColor}`}>
                    Échelle : 1 cm = {pxPerCm} px
                </p>
            )}
        </div>
    );
});

export default ProgrammeConstructionRenderer;
